/**
 * /expense/approvals + /me/expense/approvals — the two-stage expense-claim
 * approval queue (ADR-0020). Dual-mounted (same component): under the admin
 * layout (HR/finance) and the employee layout (a non-HR employee who is
 * someone's line manager — the sticky-approver pattern; the API self-filters by
 * approverUserId so a non-approver simply sees an empty queue).
 *
 *   Tab 1 "Pending my approval" — the caller's queue. The backend returns the
 *      manager-stage claims routed to the caller PLUS the finance-stage queue if
 *      the caller is finance/HR; each card shows the stage-appropriate action.
 *   Tab 2 "All pending"         — the full pending queue (both stages), HR/finance
 *      only (mirrors the API's view=all gate).
 *
 * Rejecting a claim tells an employee "the company won't reimburse this cost" —
 * a money-out, cross-party decision — so the reason is MANDATORY (ReasonModal).
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrencyCode } from '@kora/types';
import { authedLayoutRoute } from './authed.js';
import { meLayoutRoute } from './meLayout.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  listApprovals,
  managerApproveClaim,
  managerRejectClaim,
  financeApproveClaim,
  financeRejectClaim,
  type ExpenseClaim,
} from '../api/expense.js';
import { ApiErrorNotice, apiErrorMessage } from '../components/ApiErrorNotice.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ReasonModal } from '../components/ReasonModal.js';
import { ClaimReceipts } from './expenseShared.js';

export const expenseApprovalsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/expense/approvals',
  component: ExpenseApprovalsPage,
});

export const meExpenseApprovalsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/expense/approvals',
  component: ExpenseApprovalsPage,
});

type View = 'pending_my_approval' | 'all';

function ExpenseApprovalsPage(): JSX.Element {
  const { user } = useAuth();
  const isPrivileged =
    user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr' || user?.role === 'finance';
  const [view, setView] = useState<View>('pending_my_approval');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['expense', 'approvals', view],
    queryFn: () => listApprovals(view),
  });
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['expense', 'approvals'] });

  const claims = query.data?.claims ?? [];

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Expense approvals</h1>
        <p className="text-sm text-gray-400">
          Reimbursement claims are approved in two stages: the line manager
          confirms the cost was legitimately incurred, then finance signs off
          that the company pays it. Each card below shows the action for its stage.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-gray-900">
        <TabButton active={view === 'pending_my_approval'} onClick={() => setView('pending_my_approval')}>
          Pending my approval
        </TabButton>
        {isPrivileged ? (
          <TabButton active={view === 'all'} onClick={() => setView('all')}>
            All pending
          </TabButton>
        ) : null}
      </nav>

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load the approval queue.</Placeholder>
      ) : claims.length === 0 ? (
        <Placeholder>Nothing pending your approval right now.</Placeholder>
      ) : (
        <div className="space-y-4">
          {claims.map((c) => (
            <ExpenseApprovalCard key={c.id} claim={c} onDecided={() => void refresh()} />
          ))}
        </div>
      )}
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 ${
        active ? 'border-white text-white' : 'border-transparent text-gray-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function ExpenseApprovalCard({
  claim,
  onDecided,
}: {
  claim: ExpenseClaim;
  onDecided: () => void;
}): JSX.Element {
  const stage: 'manager' | 'finance' = claim.status === 'pending_finance' ? 'finance' : 'manager';
  const [rejecting, setRejecting] = useState(false);

  const approveMut = useMutation({
    mutationFn: () => (stage === 'finance' ? financeApproveClaim(claim.id) : managerApproveClaim(claim.id)),
    onSuccess: () => onDecided(),
  });
  const rejectMut = useMutation({
    mutationFn: (reason: string) =>
      stage === 'finance' ? financeRejectClaim(claim.id, { reason }) : managerRejectClaim(claim.id, { reason }),
    onSuccess: () => {
      setRejecting(false);
      onDecided();
    },
  });

  const name = claim.employee
    ? `${claim.employee.firstName} ${claim.employee.lastName}`
    : claim.employeeId;

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-white">
            {name}
            {claim.employee ? (
              <span className="text-gray-500 font-mono text-xs ml-2">{claim.employee.employeeNumber}</span>
            ) : null}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {claim.category} · incurred {formatDate(claim.incurredDate)}
          </p>
          <p className="text-sm text-gray-300 mt-1">{claim.description}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-white font-mono">{formatMoney(claim.amount, claim.currency as CurrencyCode)}</p>
          <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">
            {stage === 'finance' ? 'finance approval' : 'manager approval'}
          </p>
        </div>
      </header>

      {/* The approver needs the receipt to decide. */}
      <ClaimReceipts claimId={claim.id} canModify={false} />

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => approveMut.mutate()}
          disabled={approveMut.isPending}
          className="rounded bg-green-700 text-white text-xs px-3 py-1 disabled:opacity-50"
        >
          {approveMut.isPending ? 'Approving…' : stage === 'finance' ? 'Approve & reimburse' : 'Approve'}
        </button>
        <button
          type="button"
          onClick={() => setRejecting(true)}
          className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900"
        >
          Reject
        </button>
      </div>
      <ApiErrorNotice error={approveMut.error} fallback="Approval failed." compact />

      <ReasonModal
        open={rejecting}
        title="Reject this claim"
        description="Tell the employee why the company won't reimburse this cost. Recorded on the audit trail."
        confirmLabel="Confirm reject"
        confirmVariant="danger"
        isSubmitting={rejectMut.isPending}
        errorMessage={apiErrorMessage(rejectMut.error, 'Rejection failed.')}
        onCancel={() => setRejecting(false)}
        onConfirm={(reason) => rejectMut.mutate(reason)}
      />
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div
      className={`rounded border p-6 text-center text-sm ${
        error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'
      }`}
    >
      {children}
    </div>
  );
}
